import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetSmsIntervalMetrics } from 'infrastructure/hooks/sms-metrics/use-get-sms-interval-metrics';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { mapAndMergeMetrics } from 'infrastructure/functions/map-and-merge-metrics';
import colors from 'assets/styles/colors';
import i18next from 'i18next';

import SmsMetricsBarChart from '../bar-chart';

import type {
  ISmsMetricData,
  ISmsMetricsFilters,
} from 'infrastructure/interfaces';

const fieldMap = {
  incoming: {
    label: i18next.t('smsTypes.incoming'),
    color: colors.orange50,
  },
  null: {
    label: i18next.t('smsTypes.incoming'),
    color: colors.orange50,
  },
  user: {
    label: i18next.t('smsTypes.user'),
    color: colors.green50,
  },
  'adherence-automated': {
    label: i18next.t('smsTypes.adherence-automated'),
    color: colors.purple50,
  },
  'automated-response': {
    label: i18next.t('smsTypes.automated-response'),
    color: colors.blue50,
  },
  unknown: {
    label: i18next.t('smsTypes.unknown'),
    color: colors.red50,
  },
  campaign: {
    label: i18next.t('smsTypes.campaign'),
    color: colors.yellow50,
  },
};

const mergeMap = {
  Inbound: ['null', 'incoming'],
};

const typeBars = Object.values(fieldMap)
  .map((f) => f.label)
  .slice(1);
const colorMapper = Object.fromEntries(
  Object.values(fieldMap).map((f) => [f.label, f.color]),
);
const labelMapper = Object.fromEntries(
  Object.entries(fieldMap).map(([key, { label }]) => [key, label]),
);

interface IProps {
  filter: ISmsMetricsFilters;
}

const AutomatedVsHandledMessagesChart: React.FC<IProps> = ({ filter }) => {
  const { t } = useTranslation();

  const { data: typeInterval, isLoading } = useGetSmsIntervalMetrics(
    filter,
    'type',
  );

  const mappedTypeInterval = useMemo(() => {
    return mapAndMergeMetrics(
      typeInterval,
      labelMapper,
      mergeMap,
    ) as ISmsMetricData[];
  }, [typeInterval]);

  const totals = useMemo(
    () =>
      Object.entries(
        mappedTypeInterval.reduce((acc, obj) => {
          for (const [key, value] of Object.entries(obj)) {
            if (typeof value === 'number') {
              acc[key] = (acc[key] || 0) + value;
            }
          }
          return acc;
        }, {} as Record<string, number>),
      ).map(([label, value]) => ({ label, value })),
    [mappedTypeInterval],
  );

  return (
    <BaseSpinWrapper spinning={isLoading}>
      <SmsMetricsBarChart
        title={t('labels.messageVolumeOverTime')}
        data={mappedTypeInterval}
        bars={typeBars}
        rangeType={filter.rangeType!}
        startTime={filter.startTime!}
        endTime={filter.endTime!}
        colors={colorMapper}
        legends={totals}
      />
    </BaseSpinWrapper>
  );
};

export default AutomatedVsHandledMessagesChart;
